import { StrmClassState } from "explorer/reducers/strm-class";
import { useEffect, useMemo, useRef, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { RouteComponentProps } from "react-router-dom";
import StrmClassPreview from "shared/assets/images/strm-class-preview.png";
import IconPlayButton from "shared/assets/images/icon-play-button-strm-class.svg";
import { ReactComponent as PlayIcon } from "shared/assets/images/play-icon.svg";
import { StrmClassVideo } from "shared/interfaces/StrmClassVideo";
import { ArtistState } from "explorer/reducers/artistas";
import { Loader } from "shared/components";
import useSubscription, {
  SubscriptionPlan,
} from "shared/hooks/useSubscription";
import "./StrmClass.scss";
import SubscriptionDefaultCTA from "shared/components/subscription/SubscriptionDefaultCTA";
import { motion } from "motion/react";
import { Carousel } from "shared/components/Carousel";

const StrmClass: React.FC<RouteComponentProps> = (props) => {
  const { history } = props;
  const { t, i18n } = useTranslation();
  const { currentPlan } = useSubscription();
  const [stageVideos, setStageVideos] = useState<
    {
      stage: number;
      videos: StrmClassVideo[];
    }[]
  >([]);
  const { videos } = useSelector((state: StrmClassState) => state.strmClass);
  const { userArtist } = useSelector((state: ArtistState) => state.artists);
  const stage = Math.min(userArtist?.stage ?? 0, 9);
  const mouseDownPosition = useRef({ x: 0, y: 0 });

  const highlightVideo = useMemo(() => {
    const currentStageVideos =
      stageVideos.find((stageVideo) => stageVideo.stage === stage)?.videos ||
      [];
    return currentStageVideos[0];
  }, [stage, stageVideos]);

  let { titulo: highlightVideoTitle } = t(
    `pages.EstagioArtista.estagio.${stage || 0}`,
  ) as any;

  const handleMouseDown = (e: React.MouseEvent) => {
    mouseDownPosition.current = { x: e.clientX, y: e.clientY };
  };

  const handleCardClick = (videoId: string) => (e: React.MouseEvent) => {
    const deltaX = Math.abs(e.clientX - mouseDownPosition.current.x);
    const deltaY = Math.abs(e.clientY - mouseDownPosition.current.y);

    // Se o mouse se moveu mais de 5px, considera como drag e não navega
    if (deltaX > 5 || deltaY > 5) {
      e.preventDefault();
      return;
    }

    history.push(`/strm-class/${videoId}`);
  };

  useEffect(() => {
    if (stage) {
      const languageFiltered = videos.filter((v) =>
        i18n.language === "pt"
          ? v.language === i18n.language
          : v.language === "en",
      );

      const stages = Array.from({ length: stage }, (_, i) => i + 1).reverse();
      const stageVideosList = stages.map((stageNumber) => ({
        stage: stageNumber,
        videos: languageFiltered.filter((v) =>
          v.tags.includes(`stage-${stageNumber}`),
        ),
      }));
      setStageVideos(stageVideosList);
    }
  }, [stage, i18n.language, videos]);

  if (!stage) return <Loader.Simple size="large" fontSize={72} />;

  return (
    <div className="strm-class">
      <motion.div
        className={`highlight-container ${!highlightVideo ? "skeleton" : ""}`}
        initial={{
          filter: "blur(4px) brightness(0.8)",
          opacity: 0,
        }}
        animate={{
          filter: "blur(0px) brightness(1)",
          opacity: 1,
        }}
        transition={{
          duration: 1.2,
          ease: "easeInOut",
        }}
        style={
          highlightVideo
            ? {
                backgroundImage: `url(${highlightVideo.thumbnailUrl})`,
              }
            : undefined
        }
      >
        <div className="overlay" />
        <div className="content">
          <motion.h2
            initial={{
              opacity: 0,
              y: 20,
            }}
            animate={{
              opacity: 1,
              y: 0,
            }}
            transition={{
              duration: 0.3,
              type: "spring",
              bounce: 0.3,
              delay: 0.2,
            }}
            className="title"
          >
            {`${t("pages.StrmClass.stage", {
              stage: stage,
            })} - ${highlightVideoTitle}`}
          </motion.h2>
          <motion.div
            initial={{
              opacity: 0,
              y: 20,
            }}
            animate={{
              opacity: 1,
              y: 0,
            }}
            transition={{
              duration: 0.3,
              type: "spring",
              bounce: 0.3,
              delay: 0.5,
            }}
            className="description"
          >
            {highlightVideo?.subtitle}
          </motion.div>
          <motion.div
            initial={{
              opacity: 0,
              y: 20,
            }}
            animate={{
              opacity: 1,
              y: 0,
            }}
            transition={{
              duration: 0.3,
              type: "spring",
              bounce: 0.3,
              delay: 0.8,
            }}
          >
            <button
              className="botao-roxo"
              onClick={() => history.push(`/strm-class/${highlightVideo?.id}`)}
              disabled={!highlightVideo}
            >
              {t("pages.StrmClass.assistir")} <PlayIcon />
            </button>
          </motion.div>
        </div>
      </motion.div>
      {stageVideos.map((stageVideos) => {
        let { titulo } = t(
          `pages.EstagioArtista.estagio.${stageVideos.stage || 0}`,
        ) as any;
        return (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{
              duration: 0.5,
              type: "spring",
              bounce: 0.3,
              delay: 1,
            }}
            key={stageVideos.stage}
            className="stage-container"
          >
            <h2 className="title">
              {`${t("pages.StrmClass.stage", {
                stage: stageVideos.stage,
              })} - ${titulo}`}
            </h2>
            <Carousel>
              {stageVideos.videos.map((video) => (
                <div
                  onMouseDown={handleMouseDown}
                  onClick={handleCardClick(video.id)}
                  key={video.id}
                  className="video-card"
                >
                  <div
                    className="background"
                    style={{
                      backgroundImage: `url(${
                        video.thumbnailUrl || StrmClassPreview
                      })`,
                    }}
                  />
                  <div className="container">
                    <h3 className="title" style={{ margin: 0 }}>
                      {video.title}
                    </h3>
                    {currentPlan === SubscriptionPlan.START &&
                    !video.videoUrl ? (
                      <SubscriptionDefaultCTA
                        title={
                          <Trans
                            i18nKey={t(
                              "components.Subscription.upgradeFeatureCTATitle",
                            )}
                            components={{
                              span: <span className="cor-destaque" />,
                            }}
                          />
                        }
                      />
                    ) : (
                      <img
                        src={IconPlayButton}
                        alt="Play Button"
                        className="play-button"
                      ></img>
                    )}
                  </div>
                </div>
              ))}
            </Carousel>
          </motion.div>
        );
      })}
    </div>
  );
};

export default StrmClass;
